/**
 * Domínio de Manejo e Gestão de Vidros no Salvamento Veicular.
 *
 * Distinção doutrinária:
 * - Vidro Laminado (para-brisa): Duas lâminas de vidro com filme interno de PVB.
 *   Exige corte mecânico (Glas-Master / corta-vidro). Ao serrar, desprende
 *   pó de sílica / micropartículas de vidro que necessitam de barreira física
 *   (cobertor maleável semi-transparente e proteção respiratória).
 * - Vidro Temperado (janelas laterais e vigia traseiro): Tratamento térmico que
 *   induz tensão interna. Ao romper, fragmenta-se em milhares de arestas pontiagudas.
 *   Exige punção no canto inferior, contenção por fita contact e escudos de proteção
 *   (cobertor maleável e escudo rígido 'Lágrima').
 */

export type TipoVidroVeicular = 'laminado' | 'temperado'
export type IdVidro =
  | 'parabrisa'
  | 'portaDianteiraEsquerda'
  | 'portaDianteiraDireita'
  | 'portaTraseiraEsquerda'
  | 'portaTraseiraDireita'
  | 'vigiaTraseiro'

export type FerramentaVidro = 'puncaoAutomatica' | 'glasMaster'

export interface DefinicaoVidro {
  id: IdVidro
  nome: string
  tipo: TipoVidroVeicular
  descricao: string
  ferramentaRecomendada: FerramentaVidro
}

export const CATALOGO_VIDROS: Record<IdVidro, DefinicaoVidro> = {
  parabrisa: {
    id: 'parabrisa',
    nome: 'Para-brisa dianteiro',
    tipo: 'laminado',
    descricao: 'Vidro laminado com película interna de PVB. Não estilhaça ao impacto; exige corte com serra manual.',
    ferramentaRecomendada: 'glasMaster',
  },
  portaDianteiraEsquerda: {
    id: 'portaDianteiraEsquerda',
    nome: 'Vidro dianteiro esquerdo (motorista)',
    tipo: 'temperado',
    descricao: 'Vidro temperado sob alta tensão. Rompe em pequenos fragmentos ao impacto de punção no canto inferior.',
    ferramentaRecomendada: 'puncaoAutomatica',
  },
  portaDianteiraDireita: {
    id: 'portaDianteiraDireita',
    nome: 'Vidro dianteiro direito (passageiro)',
    tipo: 'temperado',
    descricao: 'Vidro temperado sob alta tensão. Proteja a vítima antes do acionamento.',
    ferramentaRecomendada: 'puncaoAutomatica',
  },
  portaTraseiraEsquerda: {
    id: 'portaTraseiraEsquerda',
    nome: 'Vidro traseiro esquerdo',
    tipo: 'temperado',
    descricao: 'Vidro temperado lateral traseiro.',
    ferramentaRecomendada: 'puncaoAutomatica',
  },
  portaTraseiraDireita: {
    id: 'portaTraseiraDireita',
    nome: 'Vidro traseiro direito',
    tipo: 'temperado',
    descricao: 'Vidro temperado lateral traseiro.',
    ferramentaRecomendada: 'puncaoAutomatica',
  },
  vigiaTraseiro: {
    id: 'vigiaTraseiro',
    nome: 'Vigia traseiro (vidro da tampa)',
    tipo: 'temperado',
    descricao: 'Vidro temperado de grande extensão com filamentos térmicos.',
    ferramentaRecomendada: 'puncaoAutomatica',
  },
}

export interface ProtecoesManejo {
  cobertorMaleavel: boolean
  lagrimaProtecao: boolean
  mascaraVitima: boolean
  oculosVitima: boolean
  mascaraSocorrista: boolean
  oculosSocorrista: boolean
  fitaContact: boolean
  sprayAgua: boolean
}

export const PROTECOES_INICIAIS: ProtecoesManejo = {
  cobertorMaleavel: false,
  lagrimaProtecao: false,
  mascaraVitima: false,
  oculosVitima: false,
  mascaraSocorrista: false,
  oculosSocorrista: false,
  fitaContact: false,
  sprayAgua: false,
}

export type EstadoManejoVidro = 'intacto' | 'quebrado' | 'cortado' | 'removido' | 'craqueladoComPelicula'
export type SentidoRemocaoPelicula = 'dentroParaFora' | 'foraParaDentro'

export interface ResultadoAcaoVidro {
  sucesso: boolean
  novoEstado: EstadoManejoVidro
  nuvemDePo: boolean
  estilhacosProjetados: boolean
  detritosNaVitima: boolean
  detritosNasProtecoes: boolean
  aguardandoRemocaoPelicula?: boolean
  alertaClinico: string | null
  mensagemDidatica: string
}

export function executarManejoVidro(
  vidroId: IdVidro,
  ferramenta: FerramentaVidro,
  protecoes: ProtecoesManejo,
  temInsulfilm: boolean = false,
): ResultadoAcaoVidro {
  const def = CATALOGO_VIDROS[vidroId]

  if (def.tipo === 'laminado') {
    if (ferramenta === 'puncaoAutomatica') {
      return {
        sucesso: false,
        novoEstado: 'intacto',
        nuvemDePo: false,
        estilhacosProjetados: false,
        detritosNaVitima: false,
        detritosNasProtecoes: false,
        alertaClinico: null,
        mensagemDidatica:
          'Incompatível: O vidro laminado possui filme interno de PVB que impede o colapso. A punção produz apenas trincas superficiais sem abrir acesso. Use a serra corta-vidro (Glas-Master).',
      }
    }

    // Ferramenta correta (Glas-Master)
    const vitimaProtegida = protecoes.cobertorMaleavel || protecoes.lagrimaProtecao
    const semProtecaoVitima = !vitimaProtegida && !protecoes.mascaraVitima
    const semMascaraEquipe = !protecoes.mascaraSocorrista

    if (semProtecaoVitima) {
      return {
        sucesso: true,
        novoEstado: 'cortado',
        nuvemDePo: true,
        estilhacosProjetados: false,
        detritosNaVitima: true,
        detritosNasProtecoes: false,
        alertaClinico:
          'CONTAMINAÇÃO CRÍTICA DA VÍTIMA: Inalação aguda de pó de vidro (sílica respirável) e depósitos de pó na face! O corte sem cobertor maleável ou máscara satura a cabine com micropartículas.',
        mensagemDidatica:
          'Falha grave de biossegurança: Serrar o para-brisa sempre dispersa pó fino de vidro. É mandatório cobrir a vítima com o cobertor maleável e máscara antes de acionar a serra.',
      }
    }

    if (semMascaraEquipe) {
      return {
        sucesso: true,
        novoEstado: 'cortado',
        nuvemDePo: true,
        estilhacosProjetados: false,
        detritosNaVitima: false,
        detritosNasProtecoes: vitimaProtegida,
        alertaClinico:
          'ALERTA OCUPACIONAL: Socorrista sem máscara PFF2/N95 inalou poeira de sílica durante o corte.',
        mensagemDidatica:
          'Corte executado. A vítima estava resguardada, mas a equipe precisa utilizar EPI respiratório ao serrar laminados.',
      }
    }

    return {
      sucesso: true,
      novoEstado: 'cortado',
      nuvemDePo: !protecoes.sprayAgua,
      estilhacosProjetados: false,
      detritosNaVitima: false,
      detritosNasProtecoes: vitimaProtegida,
      alertaClinico: null,
      mensagemDidatica: protecoes.sprayAgua
        ? 'Excelente técnica: O spray de água assentou o pó de sílica e o cobertor semi-transparente manteve a vítima 100% protegida.'
        : 'Corte realizado com sucesso. A vítima foi protegida pelo cobertor maleável. Dica: use spray de água para abater a poeira de vidro.',
    }
  }

  // Vidro temperado
  if (ferramenta === 'glasMaster') {
    return {
      sucesso: false,
      novoEstado: 'quebrado',
      nuvemDePo: false,
      estilhacosProjetados: true,
      detritosNaVitima: !protecoes.cobertorMaleavel && !protecoes.lagrimaProtecao,
      detritosNasProtecoes: protecoes.cobertorMaleavel || protecoes.lagrimaProtecao,
      alertaClinico: 'RISCO OPERACIONAL: Tentativa de serrar vidro temperado provocou quebra irregular e violenta.',
      mensagemDidatica:
        'Vidro temperado não se serra: use punção de centro automática no canto inferior com o vidro previamente adesivado com fita contact.',
    }
  }

  // Punção no temperado COM INSULFILM
  if (temInsulfilm) {
    return {
      sucesso: true,
      novoEstado: 'craqueladoComPelicula',
      nuvemDePo: false,
      estilhacosProjetados: false,
      detritosNaVitima: false,
      detritosNasProtecoes: false,
      aguardandoRemocaoPelicula: true,
      alertaClinico: null,
      mensagemDidatica:
        'Efeito Insulfilm (Película): O vidro temperado craquelou completamente, mas todos os milhares de estilhaços continuam colados na película! Agora execute a retirada da placa no sentido correto.',
    }
  }

  // Punção automática no temperado SEM INSULFILM
  const vitimaDesprotegida = !protecoes.cobertorMaleavel && !protecoes.lagrimaProtecao

  if (vitimaDesprotegida) {
    return {
      sucesso: true,
      novoEstado: 'quebrado',
      nuvemDePo: false,
      estilhacosProjetados: true,
      detritosNaVitima: true,
      detritosNasProtecoes: false,
      alertaClinico:
        'TRAUMA DIRETO NA VÍTIMA: Múltiplas lacerações na face e tronco por estilhaços de vidro temperado projetados para o interior! Risco de perfuração ocular.',
      mensagemDidatica:
        'Erro de manejo: Jamais rompa vidro temperado sem proteger a vítima com o cobertor maleável semi-transparente e o escudo rígido "Lágrima".',
    }
  }

  if (protecoes.fitaContact) {
    return {
      sucesso: true,
      novoEstado: 'quebrado',
      nuvemDePo: false,
      estilhacosProjetados: false,
      detritosNaVitima: false,
      detritosNasProtecoes: true,
      alertaClinico: null,
      mensagemDidatica:
        'Técnica de excelência: A fita contact conteve os estilhaços em peça única (efeito manta), o escudo "Lágrima" resguardou o habitáculo e o cobertor maleável protegeu a vítima.',
    }
  }

  return {
    sucesso: true,
    novoEstado: 'quebrado',
    nuvemDePo: false,
    estilhacosProjetados: true,
    detritosNaVitima: false,
    detritosNasProtecoes: true,
    alertaClinico: null,
    mensagemDidatica:
      'Vidro rompido com segurança. O cobertor/escudo Lágrima evitou que os fragmentos atingissem a vítima (cacos retidos sobre a proteção).',
  }
}

/**
 * Remove a placa de vidro temperado craquelada que possui película (insulfilm).
 * Doutrina CBMMG: A placa deve ser deslocada de dentro para fora para cair no solo externo.
 */
export function removerPeliculaCraquelada(
  vidroId: IdVidro,
  sentido: SentidoRemocaoPelicula,
  protecoes: ProtecoesManejo,
): ResultadoAcaoVidro {
  const vitimaProtegida = protecoes.cobertorMaleavel || protecoes.lagrimaProtecao

  if (sentido === 'dentroParaFora') {
    return {
      sucesso: true,
      novoEstado: 'removido',
      nuvemDePo: false,
      estilhacosProjetados: false,
      detritosNaVitima: false,
      detritosNasProtecoes: false,
      alertaClinico: null,
      mensagemDidatica:
        'Técnica correta CBMMG: O socorrista empurrou a placa de dentro para fora, fazendo a lâmina inteira cair no solo externo sem qualquer contato com a vítima.',
    }
  }

  return {
    sucesso: false,
    novoEstado: 'removido',
    nuvemDePo: false,
    estilhacosProjetados: true,
    detritosNaVitima: !vitimaProtegida,
    detritosNasProtecoes: vitimaProtegida,
    alertaClinico: vitimaProtegida
      ? 'AVISO OPERACIONAL: A lâmina com película colapsou para dentro, mas o cobertor maleável amparou o impacto sobre a vítima.'
      : 'ERRO TÁTICO GRAVE: A lâmina de vidro craquelada desabou diretamente sobre o corpo da vítima desprotegida, provocando contusão e cortes sob pressão.',
    mensagemDidatica:
      'Sentido de remoção incorreto: Jamais empurre vidro com película de fora para dentro. Ele deve ser projetado ou puxado para o lado externo do veículo.',
  }
}

